import React, { useState } from 'react';
import {
  Header,
  Page,
  Content,
  ContentHeader,
  InfoCard,
  Progress,
  SupportButton,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Grid,
  Tabs,
  Tab,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  LinearProgress,
  makeStyles,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import BuildIcon from '@material-ui/icons/Build';
import DnsIcon from '@material-ui/icons/Dns';
import FavoriteIcon from '@material-ui/icons/Favorite';
import DeveloperModeIcon from '@material-ui/icons/DeveloperMode';
import CodeIcon from '@material-ui/icons/Code';
import { Link as RouterLink } from 'react-router-dom';
import { useSystemInfo } from '../../buildInfo';

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(3),
  },
  valueBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  codeValue: {
    fontFamily: 'monospace',
    fontWeight: 600,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  statusGreen: {
    backgroundColor: theme.palette.type === 'dark' ? '#064e3b' : '#d1fae5',
    color: theme.palette.type === 'dark' ? '#a7f3d0' : '#065f46',
  },
  statusYellow: {
    backgroundColor: theme.palette.type === 'dark' ? '#78350f' : '#fef3c7',
    color: theme.palette.type === 'dark' ? '#fde68a' : '#92400e',
  },
  statusRed: {
    backgroundColor: theme.palette.type === 'dark' ? '#7f1d1d' : '#fee2e2',
    color: theme.palette.type === 'dark' ? '#fecaca' : '#991b1b',
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  dotGreen: {
    backgroundColor: '#10b981',
    boxShadow: '0 0 6px #10b981',
  },
  dotYellow: {
    backgroundColor: '#f59e0b',
  },
  dotRed: {
    backgroundColor: '#ef4444',
  },
  toolCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  },
  memoryBarContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  jsonViewer: {
    backgroundColor: theme.palette.type === 'dark' ? '#0f172a' : '#f8fafc',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1e293b',
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    overflowX: 'auto',
    maxHeight: 400,
  },
  headerChips: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  authProvidersBox: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.25),
    },
  },
}));

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label}`} arrow>
      <IconButton size="small" onClick={handleCopy}>
        {copied ? (
          <CheckIcon fontSize="small" style={{ color: '#10b981' }} />
        ) : (
          <FileCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function formatUptime(seconds: number): string {
  if (!seconds) return 'Just started';
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

export const SystemInfoPage: React.FC = () => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const { data, loading, error } = useSystemInfo();

  if (loading && !data) {
    return (
      <Page themeId="tool">
        <Header
          title="System & Developer Diagnostics"
          subtitle="Sulmara Platform IDP Runtime & Build Information"
        />
        <Content>
          <Progress />
        </Content>
      </Page>
    );
  }

  const build = data?.build;
  const k8s = data?.kubernetes;
  const runtime = data?.runtime;
  const subsystems = data?.subsystems;

  const commitUrl =
    build?.projectUrl && build?.commitSha && build.commitSha !== 'development'
      ? `${build.projectUrl}/-/commit/${build.commitSha}`
      : null;

  const pipelineUrl =
    build?.pipelineUrl ||
    (build?.projectUrl && build?.pipelineId
      ? `${build.projectUrl}/-/pipelines/${build.pipelineId}`
      : null);

  const memPercent =
    runtime?.memory && runtime.memory.heapTotalMb > 0
      ? Math.round(
          (runtime.memory.heapUsedMb / runtime.memory.heapTotalMb) * 100,
        )
      : 0;

  return (
    <Page themeId="tool">
      <Header
        title="System & Developer Diagnostics"
        subtitle={`Sulmara Platform IDP • ${
          build?.imageTag || build?.commitShortSha || 'v1.0.0'
        } (${k8s?.clusterName || 'indectron'})`}
      >
        <Button
          variant="contained"
          color="default"
          startIcon={<RefreshIcon />}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
        <SupportButton>
          Platform Infrastructure Diagnostics & Developer Inspector
        </SupportButton>
      </Header>

      <Content>
        {error && <ResponseErrorPanel error={error} />}

        <ContentHeader title="Instance Overview">
          <div className={classes.headerChips}>
            <Chip
              label={`Build: ${
                build?.imageTag || build?.commitShortSha || 'local'
              }`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`Host: ${k8s?.nodeName || 'local-machine'}`}
              variant="outlined"
            />
          </div>
        </ContentHeader>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<BuildIcon />} label="GitLab Build & Release" />
          <Tab icon={<DnsIcon />} label="Kubernetes & Runtime" />
          <Tab icon={<FavoriteIcon />} label="Subsystems & Health" />
          <Tab icon={<DeveloperModeIcon />} label="Developer Tools" />
        </Tabs>

        {/* Tab 0: Build & Release */}
        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard title="GitLab CI/CD Build Information">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Image Tag</strong>
                        </TableCell>
                        <TableCell>
                          <div className={classes.valueBox}>
                            <Typography className={classes.codeValue}>
                              {build?.imageTag || 'local-dev'}
                            </Typography>
                            {build?.imageTag && (
                              <CopyButton
                                text={build.imageTag}
                                label="Image Tag"
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Git Commit</strong>
                        </TableCell>
                        <TableCell>
                          <div className={classes.valueBox}>
                            <Typography className={classes.codeValue}>
                              {build?.commitSha
                                ? `${build.commitSha.substring(0, 8)} (${
                                    build.commitSha
                                  })`
                                : 'development'}
                            </Typography>
                            {build?.commitSha && (
                              <CopyButton
                                text={build.commitSha}
                                label="Git Commit SHA"
                              />
                            )}
                            {commitUrl && (
                              <Tooltip title="Open commit in GitLab" arrow>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={commitUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Git Branch / Ref</strong>
                        </TableCell>
                        <TableCell>
                          <Chip label={build?.branch || 'main'} size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Pipeline Execution</strong>
                        </TableCell>
                        <TableCell>
                          <div className={classes.valueBox}>
                            <Typography className={classes.codeValue}>
                              {build?.pipelineIid
                                ? `Pipeline #${build.pipelineIid}`
                                : build?.pipelineId
                                ? `#${build.pipelineId}`
                                : 'Local manual execution'}
                            </Typography>
                            {pipelineUrl && (
                              <Tooltip title="View pipeline in GitLab" arrow>
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={pipelineUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Build Timestamp</strong>
                        </TableCell>
                        <TableCell>
                          {build?.buildTime ? (
                            <>
                              <div>
                                {new Date(build.buildTime).toUTCString()}
                              </div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Local:{' '}
                                {new Date(build.buildTime).toLocaleString()}
                              </Typography>
                            </>
                          ) : (
                            'N/A'
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoCard title="GitOps & Container Registry">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Container Registry</strong>
                        </TableCell>
                        <TableCell>
                          <code>
                            registry.gitlab.com/sulmara/platform/core/backstage
                          </code>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>GitLab Repository</strong>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CodeIcon />}
                            endIcon={<OpenInNewIcon />}
                            component="a"
                            href={
                              build?.projectUrl ||
                              'https://gitlab.com/sulmara/platform/core/backstage'
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            sulmara/platform/core/backstage
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Flux Image Automation</strong>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            Tracked via Flux <code>ImagePolicy</code> (pattern:{' '}
                            <code>^[0-9]+-[a-f0-9]+$</code>)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>
          </Grid>
        )}

        {/* Tab 1: Kubernetes & Runtime */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard title="Kubernetes Pod & Cluster Context">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Cluster Name</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={k8s?.clusterName || 'indectron-cluster'}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Host Node</strong>
                        </TableCell>
                        <TableCell>
                          <div className={classes.valueBox}>
                            <Typography className={classes.codeValue}>
                              {k8s?.nodeName || 'local-host'}
                            </Typography>
                            {k8s?.nodeName && (
                              <CopyButton
                                text={k8s.nodeName}
                                label="Node Name"
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Pod Name</strong>
                        </TableCell>
                        <TableCell>
                          <div className={classes.valueBox}>
                            <Typography className={classes.codeValue}>
                              {k8s?.podName || 'local-process'}
                            </Typography>
                            {k8s?.podName && (
                              <CopyButton text={k8s.podName} label="Pod Name" />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Namespace & IP</strong>
                        </TableCell>
                        <TableCell>
                          <code>{k8s?.podNamespace || 'local'}</code> @{' '}
                          <code>{k8s?.podIp || '127.0.0.1'}</code>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Process Uptime</strong>
                        </TableCell>
                        <TableCell>
                          <strong>
                            {runtime?.uptimeSeconds !== undefined
                              ? formatUptime(runtime.uptimeSeconds)
                              : 'N/A'}
                          </strong>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoCard title="Node.js Process & Memory">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>Node.js Engine</strong>
                        </TableCell>
                        <TableCell>
                          <code>{runtime?.nodeVersion || process.version}</code>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Environment</strong>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={runtime?.environment || 'production'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>V8 Heap Memory</strong>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {runtime?.memory?.heapUsedMb || 0} MB /{' '}
                            {runtime?.memory?.heapTotalMb || 0} MB ({memPercent}
                            %)
                          </Typography>
                          <div className={classes.memoryBarContainer}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(memPercent, 100)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Process RSS</strong>
                        </TableCell>
                        <TableCell>{runtime?.memory?.rssMb || 0} MB</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>OS & CPU Architecture</strong>
                        </TableCell>
                        <TableCell>
                          {runtime?.os?.platform} ({runtime?.os?.arch}) •{' '}
                          {runtime?.os?.cpus || 1} vCPUs •{' '}
                          {runtime?.os?.totalMemoryGb || 0} GB Host RAM
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>
          </Grid>
        )}

        {/* Tab 2: Subsystems & Health */}
        {activeTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard title="Database & Caching Layer">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>PostgreSQL Database</strong>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`${classes.statusBadge} ${
                              subsystems?.database?.status === 'connected'
                                ? classes.statusGreen
                                : classes.statusRed
                            }`}
                          >
                            <span
                              className={`${classes.statusDot} ${
                                subsystems?.database?.status === 'connected'
                                  ? classes.dotGreen
                                  : classes.dotRed
                              }`}
                            />
                            {subsystems?.database?.status === 'connected'
                              ? `Connected (${subsystems.database.latencyMs}ms ping)`
                              : 'Disconnected / Error'}
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>DB Client Engine</strong>
                        </TableCell>
                        <TableCell>
                          <code>{subsystems?.database?.client || 'pg'}</code>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Redis Cache</strong>
                        </TableCell>
                        <TableCell>
                          <div
                            className={`${classes.statusBadge} ${
                              subsystems?.redis?.status === 'configured'
                                ? classes.statusGreen
                                : classes.statusYellow
                            }`}
                          >
                            <span
                              className={`${classes.statusDot} ${
                                subsystems?.redis?.status === 'configured'
                                  ? classes.dotGreen
                                  : classes.dotYellow
                              }`}
                            />
                            {subsystems?.redis?.status === 'configured'
                              ? 'Configured (Active)'
                              : 'Unconfigured'}
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoCard title="Storage & Identity Services">
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <strong>TechDocs Publisher</strong>
                        </TableCell>
                        <TableCell>
                          <code>
                            {subsystems?.techdocs?.publisherType || 'awsS3'}
                          </code>
                          {subsystems?.techdocs?.s3Endpoint && (
                            <div>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {subsystems.techdocs.s3Endpoint}
                              </Typography>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <strong>Auth Providers</strong>
                        </TableCell>
                        <TableCell>
                          {subsystems?.auth?.providers &&
                          subsystems.auth.providers.length > 0 ? (
                            <div className={classes.authProvidersBox}>
                              {subsystems.auth.providers.map(p => (
                                <Chip
                                  key={p}
                                  label={p}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </div>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Guest / Default
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Grid>
          </Grid>
        )}

        {/* Tab 3: Developer Tools */}
        {activeTab === 3 && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Card className={classes.toolCard} variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    App Visualizer
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Inspect frontend plugin tree, extension points, blueprints,
                    and route bindings across the Backstage app.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/app-visualizer"
                    startIcon={<DeveloperModeIcon />}
                  >
                    Open App Visualizer
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card className={classes.toolCard} variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Flux GitOps Runtime
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    View real-time Flux CD Kustomizations, HelmReleases, and Git
                    reconciliation states on Indectron cluster.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/flux-runtime"
                    startIcon={<DnsIcon />}
                  >
                    Open Flux Runtime
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card className={classes.toolCard} variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Scaffolder Templates
                  </Typography>
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Inspect software templates and custom actions (including
                    SOPS Age encryption actions) in Drydock.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/create"
                    startIcon={<BuildIcon />}
                  >
                    Explore Templates
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <InfoCard title="Raw Diagnostics JSON (Safe Payload)">
                <pre className={classes.jsonViewer}>
                  {JSON.stringify(data, null, 2)}
                </pre>
              </InfoCard>
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
